import { serialize } from "object-to-formdata";
import Client from "./client";

import {
  AccountDetails,
  ApiResponse,
  CreateTicketData,
  CreateSupportNotificationRequestProps,
  ErrorApiResponse,
  EscalateTicketProps,
  GetArticleByIdResponse,
  GetCategoriesResponse,
  GetCategoryByIdResponse,
  GetSectionByIdResponse,
  GetTicketQueryParams,
  GetSupportPlanResponse,
  GetUser,
  SearchResultsResponseData,
  SearchSuggestionResults,
  SendUserFeedbackResponse,
  SupportApiResponse,
  supportNotificationRequest,
  TicketFieldByIdData,
  UpdateTicketArgs,
  TicketHistoryResponse,
  TicketSubmissionData,
  UpdateSupportPlanArgs,
  UserDetails,
  UserFeedbackArgs,
  SystemStatusResponse,
  UserAccountsResponse,
  GetSubAccountsResponse,
  RecaptchaAction,
  LoggerInterface,
  GetCategoryByIdProps,
  GetSectionsByIdProps,
  DEFAULT_HEADERS,
  SUPPORT_API_LOG_PREFIX,
  getOscConfig,
  TicketDetailsAttributes,
  SupportPlanResponse,
  PersonalizedSupportFormValues,
} from "@twiliointernal/e2-commons-shared-definitions";
import { getRecaptchaToken } from "@twiliointernal/e2-commons-shared-utils";

const { searchIndex, supportApiEndpoint } = getOscConfig();

class SupportApi extends Client {
  logger?: LoggerInterface;
  user?: UserDetails;
  account?: AccountDetails;
  accounts?: AccountDetails[];
  subaccountsMetadata?: GetSubAccountsResponse;
  isOnSubaccount: boolean;

  constructor() {
    super(supportApiEndpoint);
    this.account = undefined;
    this.user = undefined;
    this.accounts = undefined;
    this.subaccountsMetadata = undefined;
    this.isOnSubaccount = false;
  }

  setUser = (user: UserDetails): void => {
    this.user = user;
  };

  setAccount = (account: AccountDetails): void => {
    this.account = account;
  };

  setAccounts = (accounts: AccountDetails[]): void => {
    this.accounts = accounts;
  };

  setSubaccountsMetadata = (
    subaccountsMetadata: GetSubAccountsResponse,
    isOnSubaccount: boolean
  ): void => {
    this.subaccountsMetadata = subaccountsMetadata;
    this.isOnSubaccount = isOnSubaccount;
  };

  setLogger(logger: LoggerInterface): void {
    this.logger = logger;
  }

  createGatedTicket = async (
    data: TicketSubmissionData
  ): Promise<SupportApiResponse<CreateTicketData>> => {
    const csrf = await this.getCsrf();

    const formData: FormData = serialize(data, {
      noFilesWithArrayNotation: true,
      noAttributesWithArrayNotation: true,
    });

    const response: ApiResponse<CreateTicketData> = await this.post({
      path: "/tickets",
      body: formData,
      headers: {
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[createGatedTicket]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<CreateTicketData>;
  };

  createUngatedTicket = async (
    data: TicketSubmissionData,
    stargateUngatedUserId: string
  ): Promise<SupportApiResponse<CreateTicketData>> => {
    const csrf = await this.getCsrf();
    // const stargateUngatedUserId = segment.getAnonymousUserId() as string;
    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.SUBMIT);

    const formData: FormData = serialize(
      {
        ...data,
        recaptchaToken,
        stargateUngatedUserId,
      },
      {
        noFilesWithArrayNotation: true,
        noAttributesWithArrayNotation: true,
      }
    );

    const response: ApiResponse<CreateTicketData> = await this.post({
      path: "/tickets/ungated",
      body: formData,
      headers: {
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[createUngatedTicket]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<CreateTicketData>;
  };

  getTicketDetails = async (
    ticketId: string
  ): Promise<SupportApiResponse<TicketDetailsAttributes>> => {
    const csrf = await this.getCsrf();

    const response: ApiResponse<TicketDetailsAttributes> = await this.get({
      path: `/tickets/${ticketId}?include=users`,
      headers: {
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getTicketDetails]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<TicketDetailsAttributes>;
  };

  updateTicket = async ({
    ticketId,
    data,
  }: UpdateTicketArgs): Promise<
    SupportApiResponse<TicketDetailsAttributes>
  > => {
    const csrf = await this.getCsrf();

    const formData: FormData = serialize(data, {
      noFilesWithArrayNotation: true,
      noAttributesWithArrayNotation: true,
    });

    this.logger?.info(
      `${SUPPORT_API_LOG_PREFIX}::[updateTicket]::[request]::${ticketId}`,
      {
        body: data,
        headers: {
          "x-twilio-csrf": csrf,
        },
      }
    );

    const response: ApiResponse<TicketDetailsAttributes> = await this.post({
      path: `/tickets/${ticketId}?include=users`,
      body: formData,
      headers: {
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[updateTicket]::${ticketId}::${JSON.stringify(
          errorResponse
        )}`,
        {
          errorResponse: JSON.stringify(errorResponse),
        }
      );
    }

    this.logger?.info(
      `${SUPPORT_API_LOG_PREFIX}::[updateTicket]::[response]::${ticketId}`,
      {
        response: JSON.stringify(response),
      }
    );

    return response as SupportApiResponse<TicketDetailsAttributes>;
  };

  escalateTicket = async ({
    ticketId,
    escalationType,
    additionalInfo,
  }: EscalateTicketProps): Promise<
    SupportApiResponse<TicketDetailsAttributes>
  > => {
    const body = JSON.stringify({
      escalationType,
      escalationReason: additionalInfo,
    });
    const csrf = await this.getCsrf();

    const response: ApiResponse<TicketDetailsAttributes> = await this.put({
      path: `/tickets/escalations/${ticketId}`,
      body,
      headers: {
        ...DEFAULT_HEADERS,
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[escalateTicket]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<TicketDetailsAttributes>;
  };

  getUser = async (): Promise<SupportApiResponse<GetUser>> => {
    /**
     * The 'redirect' option needs to stay as 'follow' to prevent the following:
     *   - 'error': Throws console error on 302 response
     *   - 'manual': Allows handling of 302 response, but causes network error
     *   - 'redirect': Magically does not redirect and stays on OSC ¯\_(ツ)_/¯
     */
    const response: ApiResponse<GetUser> = await this.get({
      path: "/user",
      requestOptions: {
        redirect: "follow",
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getUser]::${JSON.stringify(
          errorResponse
        )}`,
        response
      );
    }

    return response as SupportApiResponse<GetUser>;
  };

  getTicketField = async (
    fieldId: string
  ): Promise<SupportApiResponse<TicketFieldByIdData>> => {
    const response: ApiResponse<TicketFieldByIdData> = await this.get({
      path: `/tickets/field/${fieldId}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getTicketField]::${JSON.stringify(
          errorResponse
        )}`
      );
    }
    return response as SupportApiResponse<TicketFieldByIdData>;
  };

  sendUserFeedback = async ({
    queryId,
    resultId,
    relevanceValue,
    clickTime,
  }: UserFeedbackArgs): Promise<
    SupportApiResponse<SendUserFeedbackResponse>
  > => {
    const csrf = await this.getCsrf();
    const baseBody = {
      queryId,
      index: searchIndex,
      clickTime: clickTime || Date.now(),
    };

    const body = JSON.stringify(
      relevanceValue ? { ...baseBody, relevanceValue } : baseBody
    );

    const response: ApiResponse<SendUserFeedbackResponse> = await this.post({
      path: `/search/${resultId}/feedback`,
      body: body,
      headers: {
        ...DEFAULT_HEADERS,
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[sendUserFeedback]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<SendUserFeedbackResponse>;
  };

  search = async (
    queryText: string
  ): Promise<SupportApiResponse<SearchResultsResponseData>> => {
    const sanitzedQueryText = encodeURIComponent(queryText);
    const response: ApiResponse<SearchResultsResponseData> = await this.get({
      path: `/search?index=${searchIndex}&queryText=${sanitzedQueryText}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[search]::${JSON.stringify(errorResponse)}`,
        response
      );
    }

    return response as SupportApiResponse<SearchResultsResponseData>;
  };

  searchSuggestions = async (
    queryText: string
  ): Promise<SupportApiResponse<SearchSuggestionResults>> => {
    const sanitizedQueryText = encodeURIComponent(queryText);
    const response: ApiResponse<SearchSuggestionResults> = await this.get({
      path: `/search/suggestions/?index=${searchIndex}&queryText=${sanitizedQueryText}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[searchSuggestions]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<SearchSuggestionResults>;
  };

  getCategories = async (): Promise<
    SupportApiResponse<GetCategoriesResponse>
  > => {
    const response: ApiResponse<GetCategoriesResponse> = await this.get({
      path: "/knowledgeBase/categories",
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getCategories]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<GetCategoriesResponse>;
  };

  getCategoryById = async ({
    categoryId,
  }: GetCategoryByIdProps): Promise<
    SupportApiResponse<GetCategoryByIdResponse>
  > => {
    const response: ApiResponse<GetCategoryByIdResponse> = await this.get({
      path: `/knowledgeBase/categories/${categoryId}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getCategoryById]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<GetCategoryByIdResponse>;
  };

  getSectionById = async ({
    sectionId,
  }: GetSectionsByIdProps): Promise<
    SupportApiResponse<GetSectionByIdResponse>
  > => {
    const response: ApiResponse<GetSectionByIdResponse> = await this.get({
      path: `/knowledgeBase/sections/${sectionId}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getSectionById]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<GetSectionByIdResponse>;
  };

  getArticleById = async (
    id: string
  ): Promise<SupportApiResponse<GetArticleByIdResponse>> => {
    const response: ApiResponse<GetArticleByIdResponse> = await this.get({
      path: `/knowledgeBase/articles/${id}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getArticleById]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<GetArticleByIdResponse>;
  };

  getSupportPlan = async (): Promise<
    SupportApiResponse<GetSupportPlanResponse>
  > => {
    const response: ApiResponse<GetSupportPlanResponse> = await this.get({
      path: "/supportPlans",
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getSupportPlan]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<GetSupportPlanResponse>;
  };

  updateSupportPlan = async (
    updateRequest: UpdateSupportPlanArgs
  ): Promise<SupportApiResponse<SupportPlanResponse>> => {
    const body = JSON.stringify(updateRequest);
    const csrf = await this.getCsrf();
    const headers = {
      "Content-type": "application/json",
      "x-twilio-csrf": csrf,
    };

    const response: ApiResponse<SupportPlanResponse> = await this.put({
      path: "/supportPlans",
      body,
      headers,
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[updateSupportPlan]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<SupportPlanResponse>;
  };

  sendPersonalizedSupportRequest = async (
    formValues: PersonalizedSupportFormValues
  ): Promise<SupportApiResponse<PersonalizedSupportFormValues>> => {
    const body = JSON.stringify(formValues);
    const csrf = await this.getCsrf();
    const headers = {
      "Content-type": "application/json",
      "x-twilio-csrf": csrf,
    };

    const response: ApiResponse<PersonalizedSupportFormValues> =
      await this.post({
        path: "/supportPlans/personalizedSupport",
        body,
        headers,
        requestOptions: {
          credentials: "include",
        },
      });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[sendPersonalizedSupportRequest]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<PersonalizedSupportFormValues>;
  };

  getTickets = async (
    queryParams: GetTicketQueryParams
  ): Promise<SupportApiResponse<TicketHistoryResponse>> => {
    const response: ApiResponse<TicketHistoryResponse> = await this.get({
      path: `/tickets?q=${JSON.stringify(queryParams)}`,
      requestOptions: {
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getTickets]::${JSON.stringify(
          errorResponse
        )}`,
        response
      );
    }

    return response as SupportApiResponse<TicketHistoryResponse>;
  };

  getSystemStatus = async (): Promise<
    SupportApiResponse<SystemStatusResponse[]>
  > => {
    const response: ApiResponse<SystemStatusResponse[]> = await this.get({
      path: "/systemStatus",
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getSystemStatus]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<SystemStatusResponse[]>;
  };

  getUserAccounts = async (): Promise<
    SupportApiResponse<UserAccountsResponse>
  > => {
    const response: ApiResponse<UserAccountsResponse> = await this.get({
      path: "/user/accounts",
      requestOptions: {
        redirect: "manual",
        credentials: "include",
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[getUserAccounts]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<UserAccountsResponse>;
  };

  createSupportNotificationRequest = async ({
    channelSid,
    phoneNumber,
  }: CreateSupportNotificationRequestProps): Promise<
    SupportApiResponse<Response>
  > => {
    const { headers, requestOptions } = await this.getRequestOptions();

    const body = {
      preferredContactMethod:
        supportNotificationRequest.preferredContactMethod.Sms,
      phoneNumber,
    };

    const response = await this.post<SupportApiResponse<Response>>({
      path: `/chat/${channelSid}/notificationRequest`,
      headers,
      body: JSON.stringify(body),
      requestOptions,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      this.logger?.warn(
        `${SUPPORT_API_LOG_PREFIX}::[createSupportNotificationRequest]::${JSON.stringify(
          errorResponse
        )}`
      );
    }

    return response as SupportApiResponse<Response>;
  };
}

export default new SupportApi();
